// frontend/src/utils/formatting.ts

import { format, parseISO, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Strip diacritics so search terms like "mun" match "Muñoz", "haland" matches
// "Håland", etc. NFD decomposes accented chars into base + combining marks,
// then we drop the combining marks (Unicode block U+0300 to U+036F).
// NFD does NOT decompose standalone letters like Ø/Ł/Đ/Æ/ß - they aren't
// letter+accent pairs - so map them explicitly (caught 2026-07-23: searching
// "odegaard" in the draft pool never matched Ødegaard).
const SPECIAL_LETTERS: Record<string, string> = {
  'ø': 'o', 'Ø': 'O', 'đ': 'd', 'Đ': 'D', 'ł': 'l', 'Ł': 'L',
  'æ': 'ae', 'Æ': 'AE', 'œ': 'oe', 'Œ': 'OE', 'ß': 'ss',
  'ð': 'd', 'Ð': 'D', 'þ': 'th', 'Þ': 'TH', 'ı': 'i',
};
export const stripAccents = (s: string | null | undefined): string =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[øØđĐłŁæÆœŒßðÐþÞı]/g, (ch) => SPECIAL_LETTERS[ch] ?? ch);

// Lowercased + accent-stripped, for case- and diacritic-insensitive substring matches.
export const foldForSearch = (s: string | null | undefined): string =>
  stripAccents(s).toLowerCase();

// --- NEW: STAT NAME AND ABBREVIATION MAPPINGS ---
export const STAT_DISPLAY_NAMES: Record<string, string> = {
  'ProgPass': 'Impact Pass',
  'ProgCarry': 'Impact Carry',
  'SCA': 'Shot Creation',
  'GCA': 'Goal Creation',
  'ShotOnTarget': 'Shot on Target',
  'Goal': 'Goal',
  'Shot': 'Shot',
  'Dispossessed': 'Dispossessed',
};

export const STAT_ABBREVIATIONS: Record<string, string> = {
  'ProgPass': 'IP',
  'ProgCarry': 'IC',
  'SCA': 'SC',
  'GCA': 'GC',
  'ShotOnTarget': 'SOT',
  'Goal': 'G',
  'Shot': 'S',
  'Dispossessed': 'DIS',
};

export const formatStatName = (apiKey: string): string => {
  return STAT_DISPLAY_NAMES[apiKey] || apiKey;
};
// --- END NEW ---

// Define a common type that captures the minimal fields needed from any player-like object
// that formatPlayerDisplayName might encounter.
export interface MinimalPlayerDisplayInfo {
  id?: number | null;       // Player's unique identifier
  player_id?: number | null; // Joined/API rows often expose player_id instead.
  name?: string | null;     // Full name, often a fallback
  nickname?: string | null; // Preferred display name
}

// ============================================================================
// DISPLAY-NAME DOCTRINE (owner rule, 2026-07-17):
// User-facing surfaces NEVER render a player's full legal name when a
// nickname exists. "Kylian Mbappé Lottin" is data; "Kylian Mbappé" is UI.
// Every new surface that shows a player name must either call this helper or
// consume an API field that already coalesces nickname->name (the pattern in
// the season-stats cruds). If an endpoint response lacks `nickname`, that is
// a backend bug — add the field, don't render `name`.
// ============================================================================
// Accepts any player-like object via the MinimalPlayerDisplayInfo interface
// (works with ApiPlayer and PlayerRead structurally).
export const formatPlayerDisplayName = (
    player: MinimalPlayerDisplayInfo | null | undefined
): string => {
  if (!player) {
    // logger.warn('[formatPlayerDisplayName] Received null or undefined player.');
    return 'N/A';
  }

  // Prioritize nickname, then name. Ensure they are actual strings after checking for null/undefined.
  const nick = typeof player.nickname === 'string' && player.nickname.trim() !== '' ? player.nickname.trim() : null;
  const fullName = typeof player.name === 'string' && player.name.trim() !== '' ? player.name.trim() : null;

  if (nick) {
    // logger.debug(`[formatPlayerDisplayName] Using nickname: '${nick}' for player ID ${player.id || 'unknown'}`);
    return nick;
  }
  if (fullName) {
    // logger.debug(`[formatPlayerDisplayName] Using full name: '${fullName}' for player ID ${player.id || 'unknown'}`);
    return fullName;
  }
  
  // Fallback if both name and nickname are effectively empty or not strings
  const playerId = player.id ?? player.player_id;
  if (playerId != null) { // Check if id is not null or undefined
    // logger.warn(`[formatPlayerDisplayName] Both nickname and name missing/empty for player ID ${player.id}. Falling back to ID.`);
    return `Player ${playerId}`;
  }
  
  // logger.warn(`[formatPlayerDisplayName] Player object missing id, name, and nickname.`);
  return 'Player N/A'; // Ultimate fallback
};


// --- formatDate (keep as is) ---
export const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "Invalid Date";
    try {
        // Assuming dateString is 'YYYY-MM-DD' and represents a date in UTC.
        // To format it as "Saturday, Apr 19" based on UTC interpretation:
        return formatInTimeZone(dateString + 'T00:00:00Z', 'UTC', 'EEEE, MMM d');
    } catch (e) {
        console.error(`Error formatting date: ${dateString}`, e);
        return "Invalid Date";
    }
};

// --- formatWeekendLabelDate (keep as is) ---
export const formatWeekendLabelDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "Invalid Date";
     try {
         // Assuming dateString is 'YYYY-MM-DD' (Saturday) and represents a date in UTC.
         return `SAT ${formatInTimeZone(dateString + 'T00:00:00Z', 'UTC', 'MMM d')}`;
     } catch (e) {
         console.error(`Error formatting weekend label date: ${dateString}`, e);
         return "Invalid Date";
     }
};

// --- formatIsoDate (keep as is) ---
export const formatIsoDate = (dateString: string | null | undefined, formatString = 'PP'): string => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString); // parseISO handles various ISO formats, including those with 'Z' or offsets
      return isValid(date) ? format(date, formatString) : 'Invalid Date'; // format will use local timezone by default
    } catch (e) {
      console.error(`Error formatting ISO date ${dateString}:`, e);
      return 'Format Error';
    }
};

// StatsBomb's `minute` field is the zero-based whole minute on the cumulative
// match clock: period 2 starts at 45, extra time at 90, etc.  Thus minute 45
// in period 1 is already the first minute of first-half stoppage time.
export const formatGametime = (period: number, minute: number): string => {
  if (typeof period !== 'number' || typeof minute !== 'number') {
    return '--';
  }

  // Period 1: minute 45 is 45+1, minute 46 is 45+2, etc.
  if (period === 1) {
    if (minute < 45) {
      return `${minute}'`;
    }
    return `45+${minute - 44}'`;
  }

  // Period 2: minute 90 is 90+1, minute 91 is 90+2, etc.
  if (period === 2) {
    if (minute < 90) {
      return `${minute}'`;
    }
    return `90+${minute - 89}'`;
  }

  // Period 3 (ET first half): minute 105 is 105+1.
  if (period === 3) {
    if (minute < 105) {
      return `${minute}'`;
    }
    return `105+${minute - 104}'`;
  }

  // Period 4 (ET second half): minute 120 is 120+1.
  if (period === 4) {
    if (minute < 120) {
      return `${minute}'`;
    }
    return `120+${minute - 119}'`;
  }

  // Period 5: Penalty shootout
  if (period === 5) {
    return 'Pens';
  }

  return `P${period}:${minute}'`;
};
// Shared formatter for waiver processing/clear instants ("Wed, Jun 17, 5:00 AM").
// One cached Intl.DateTimeFormat: constructing it per call is the expensive part
// and the player pool renders this per row.
const waiverDateTimeFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
const waiverDateLabelCache = new Map<string, string>();

export const formatWaiverProcessDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const cached = waiverDateLabelCache.get(iso);
  if (cached !== undefined) return cached;
  const d = new Date(iso);
  const label = isNaN(d.getTime()) ? '' : waiverDateTimeFormat.format(d);
  if (waiverDateLabelCache.size > 200) waiverDateLabelCache.clear();
  waiverDateLabelCache.set(iso, label);
  return label;
};

// Exact localized deadline plus relative context ("Thu at 7:35 PM - in 18h")
// per TRADE-REVIEW.md 5a. Relative part is coarse (d/h/m) so a once-per-minute
// re-render is enough; hyphen separator per app copy rules.
const deadlineDayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const deadlineTimeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

export const formatDeadlineWithRelative = (
  iso: string | null | undefined,
  nowMs: number = Date.now(),
): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const exact = `${deadlineDayFormat.format(d)} at ${deadlineTimeFormat.format(d)}`;
  const diffMs = d.getTime() - nowMs;
  if (diffMs <= 0) return exact;
  const minutes = Math.round(diffMs / 60000);
  const relative = minutes < 60
    ? `in ${Math.max(1, minutes)}m`
    : minutes < 48 * 60
      ? `in ${Math.round(minutes / 60)}h`
      : `in ${Math.round(minutes / (60 * 24))}d`;
  return `${exact} - ${relative}`;
};

// Compact weekday label for the pool's W chip ("W - Wed"). Same caching
// rationale as above: rendered per row.
const waiverDayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const waiverDayLabelCache = new Map<string, string>();

export const formatWaiverProcessDay = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const cached = waiverDayLabelCache.get(iso);
  if (cached !== undefined) return cached;
  const d = new Date(iso);
  const label = isNaN(d.getTime()) ? '' : waiverDayFormat.format(d);
  if (waiverDayLabelCache.size > 200) waiverDayLabelCache.clear();
  waiverDayLabelCache.set(iso, label);
  return label;
};
